import * as _ from 'lodash';
import { Axle, CabinDimensions, Position } from 'src/app/core/types';
import {
  BoxGeometry,
  BufferGeometry,
  CircleGeometry,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  EdgesGeometry,
  Float32BufferAttribute,
  Group,
  Line,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  PlaneGeometry,
  RingGeometry,
  Scene,
  Vector3,
} from 'three';
import RenderingContext from './rendering-context';

interface LoadingSpaceVisualisationParts {
  front: Object3D;
  basis: Object3D;
  back: Object3D;
  sides: Object3D;
  axis: Object3D;
  backDoors: Object3D;
  measureVertical: Object3D;
  measureHorizontal: Object3D;
  measureHeight: Object3D;
  varioFloor?: Object3D;
  cooler?: Object3D;
  wheelArch?: Object3D;
  airContainer?: Object3D;
  pallet?: Object3D;
  containerDoor?: Object3D;
  gravityCenter?: Object3D;
}

class LoadingSpaceVisualisationParts {}

export class LoadingSpace implements LoadingSpace {
  public measureGroup: Group;
  public readonly gap: number = 2000;
  public readonly MEASURE_WIDTH: number = 2000;

  public readonly lineMaterial: LineBasicMaterial = new LineBasicMaterial({
    color: 0x508de1,
    side: DoubleSide,
    transparent: true,
    opacity: 1,
  });
  public readonly markupMaterial: MeshBasicMaterial = new MeshBasicMaterial({
    color: 0xfccb08,
    side: DoubleSide,
    transparent: true,
    opacity: 1,
  });
  public readonly lineGreyMaterial: LineBasicMaterial = new LineBasicMaterial({
    color: 0xb7becf,
    side: DoubleSide,
    transparent: true,
    opacity: 1,
  });
  public readonly lineGreenTransparentMaterial: LineBasicMaterial = new LineBasicMaterial({
    color: 0xd5f5de,
    side: DoubleSide,
  });
  public readonly lineRedTransparentMaterial: LineBasicMaterial = new LineBasicMaterial({
    color: 0xf7d7db,
    side: DoubleSide,
  });
  public readonly meshMaterial: MeshBasicMaterial = new MeshBasicMaterial({
    color: 0x508de1,
    side: DoubleSide,
    transparent: true,
    opacity: 1,
  });
  public readonly meshTransparentMaterial: MeshBasicMaterial = new MeshBasicMaterial({
    color: 0x508de1,
    side: DoubleSide,
    transparent: true,
    opacity: 0,
    depthWrite: false,
  });
  public readonly gravityArrowMaterial: MeshBasicMaterial = new MeshBasicMaterial({
    color: 0x000000,
    side: DoubleSide,
    transparent: false,
  });
  public readonly basisMaterial: MeshBasicMaterial = new MeshBasicMaterial({
    color: 0x508de1,
    side: DoubleSide,
    transparent: true,
    opacity: 0.6,
    depthWrite: false,
  });
  public readonly frontMaterial: MeshBasicMaterial = new MeshBasicMaterial({
    color: 0x508de1,
    transparent: true,
    opacity: 0.3,
    side: DoubleSide,
    depthWrite: false,
  });

  visualisationParts: LoadingSpaceVisualisationParts;
  private setVisualisationParts: LoadingSpaceVisualisationParts;

  constructor(
    public name: string,
    public length: number,
    public width: number,
    public height: number,
    public limit: number,
    public length1: number,
    public width1: number,
    public height1: number,
    public limit1: number,
    public set: boolean,
    public type: number,
    public axis: Axle[] = [],
    public dimensionsOfCabin: CabinDimensions,
    public varioTrailerFloorHeight?: number,
    public varioSemiTrailerFloorHeight?: number,
    public varioTrailerFloorThickness?: number,
    public varioSemiTrailerFloorThickness?: number,
    public varioTrailerFloorCapacity?: number,
    public varioSemiTrailerFloorCapacity?: number,
    public coolerTrailerWidth?: number,
    public coolerTrailerHeight?: number,
    public coolerTrailerLength?: number,
    public coolerTrailerFloorHeight?: number,
    public coolerSemiTrailerWidth?: number,
    public coolerSemiTrailerHeight?: number,
    public coolerSemiTrailerLength?: number,
    public coolerSemiTrailerFloorHeight?: number,
    public wheelTrailerWidth?: number,
    public wheelTrailerHeight?: number,
    public wheelTrailerLength?: number,
    public wheelTrailerDistance?: number,
    public wheelSemiTrailerWidth?: number,
    public wheelSemiTrailerHeight?: number,
    public wheelSemiTrailerLength?: number,
    public wheelSemiTrailerDistance?: number,
    public unit?: string,
    public airType?: number,
    public airLengthA?: number,
    public airWidth?: number,
    public airBaseB?: number,
    public airHeightC?: number,
    public airHeightD?: number,
    public airLoadCapacity?: number,
    public trailerLoadingSide?: string,
    public semiTrailerLoadingSide?: string,
    public airQuantity?: number,
    public trailerDoorWidth?: number,
    public trailerDoorHeight?: number,
    public semiTrailerDoorWidth?: number,
    public semiTrailerDoorHeight?: number,
    public view?: string,
    public palletMaxHeight?: number,
    public palletHeight?: number,
    public gravityShowCenter?: boolean,
    public gravitySemiTrailerLengthTolerance?: number,
    public gravitySemiTrailerWidthTolerance?: number,
    public gravityTrailerLengthTolerance?: number,
    public gravityTrailerWidthTolerance?: number,
    public trailerContainerWeight?: number,
    public semiTrailerContainerWeight?: number,
    public trailerCOGX?: number,
    public trailerCOGY?: number,
    public trailerCOGZ?: number,
    public semiTrailerCOGX?: number,
    public semiTrailerCOGY?: number,
    public semiTrailerCOGZ?: number,
  ) {
    this.times100();
  }

  public draw(
    renderingContext: RenderingContext<OrthographicCamera | PerspectiveCamera>,
    printMode?: boolean,
  ): void {
    this.visualisationParts = new LoadingSpaceVisualisationParts();
    if (this.type === 4) {
      const offsetX = (this.airLengthA - this.airBaseB) / 2;
      const offsetY = 0;
      const offset = this.airType == 1 ? offsetX : offsetY;
      this.visualisationParts.basis = this.drawBasis(0, 0, this.airWidth, this.airBaseB);
      this.visualisationParts.basis.position.set(0, 0, offset);
      renderingContext.scene.add(this.visualisationParts.basis);
      this.visualisationParts.sides = this.drawAirContainer();
      this.visualisationParts.sides.position.set(0, 0, offset);
      renderingContext.scene.add(this.visualisationParts.sides);
      if (this.gravityShowCenter) {
        this.visualisationParts.gravityCenter = this.drawGravityCenter();
        renderingContext.scene.add(this.visualisationParts.gravityCenter);
      }
    }
    if (this.type !== 4) {
      if (this.view !== 'setBack') {
        if (this.type !== 3) {
          this.visualisationParts.sides = this.drawSides();
          renderingContext.scene.add(this.visualisationParts.sides);
          this.visualisationParts.basis = this.drawBasis();
          renderingContext.scene.add(this.visualisationParts.basis);
          this.visualisationParts.back = this.drawBack();
          renderingContext.scene.add(this.visualisationParts.back);
          this.visualisationParts.front = this.drawFront();
          renderingContext.scene.add(this.visualisationParts.front);
        }
      }
      if (this.type === 2) {
        if (
          this.view !== 'setLeft' &&
          this.view !== 'left' &&
          this.view !== 'back' &&
          this.gravityShowCenter
        ) {
          this.visualisationParts.gravityCenter = this.drawGravityCenter();
          renderingContext.scene.add(this.visualisationParts.gravityCenter);
        }

        if (this.trailerDoorWidth && this.view !== 'setBack') {
          this.visualisationParts.containerDoor = this.drawContainerDoor(false);
          renderingContext.scene.add(this.visualisationParts.containerDoor);
        }
      }

      if (this.type === 3) {
        this.visualisationParts.basis = this.drawBasis(this.palletHeight);
        renderingContext.scene.add(this.visualisationParts.basis);
        this.visualisationParts.pallet = this.drawPallet();
        renderingContext.scene.add(this.visualisationParts.pallet);
      }

      if (this.varioTrailerFloorHeight) {
        this.visualisationParts.varioFloor = this.drawVarioFloor(
          this.varioTrailerFloorHeight,
          this.varioTrailerFloorThickness,
        );
        renderingContext.scene.add(this.visualisationParts.varioFloor);
      }

      if (!printMode && this.wheelTrailerWidth) {
        const wheelData = {
          wheelTrailerWidth: this.wheelTrailerWidth,
          wheelTrailerHeight: this.wheelTrailerHeight,
          wheelTrailerLength: this.wheelTrailerLength,
          wheelTrailerDistance: this.wheelTrailerDistance,
          wheelSemiTrailerWidth: this.wheelSemiTrailerWidth,
          wheelSemiTrailerHeight: this.wheelSemiTrailerHeight,
          wheelSemiTrailerLength: this.wheelSemiTrailerLength,
          wheelSemiTrailerDistance: this.wheelSemiTrailerDistance,
        };
        this.visualisationParts.wheelArch = this.drawWheelArch(wheelData);
        renderingContext.scene.add(this.visualisationParts.wheelArch);
      }
      if (!printMode && this.coolerTrailerWidth) {
        const coolerData = {
          coolerTrailerWidth: this.coolerTrailerWidth,
          coolerTrailerHeight: this.coolerTrailerHeight,
          coolerTrailerLength: this.coolerTrailerLength,
          coolerTrailerFloorHeight: this.coolerTrailerFloorHeight,
          coolerSemiTrailerWidth: this.coolerSemiTrailerWidth,
          coolerSemiTrailerHeight: this.coolerSemiTrailerHeight,
          coolerSemiTrailerLength: this.coolerSemiTrailerLength,
          coolerSemiTrailerFloorHeight: this.coolerSemiTrailerFloorHeight,
        };
        this.visualisationParts.cooler = this.drawCooler(coolerData);
        renderingContext.scene.add(this.visualisationParts.cooler);
      }
      if (this.axis.length > 1) {
        this.visualisationParts.axis = this.drawAxles();
        renderingContext.scene.add(this.visualisationParts.axis);
      }

      if (this.set && this.view !== 'back') {
        this.setVisualisationParts = new LoadingSpaceVisualisationParts();
        this.setVisualisationParts.sides = this.drawSetSides();
        renderingContext.scene.add(this.setVisualisationParts.sides);
        this.setVisualisationParts.basis = this.drawSetBasis();
        renderingContext.scene.add(this.setVisualisationParts.basis);
        this.setVisualisationParts.front = this.drawSetFront();
        renderingContext.scene.add(this.setVisualisationParts.front);
        this.setVisualisationParts.back = this.drawSetBack();
        renderingContext.scene.add(this.setVisualisationParts.back);
        if (this.varioSemiTrailerFloorHeight) {
          this.setVisualisationParts.varioFloor = this.drawVarioFloor(
            this.varioSemiTrailerFloorHeight,
            this.varioSemiTrailerFloorThickness,
            true,
          );
          renderingContext.scene.add(this.setVisualisationParts.varioFloor);
        }
        if (this.semiTrailerDoorWidth) {
          this.setVisualisationParts.containerDoor = this.drawContainerDoor(true);
          renderingContext.scene.add(this.setVisualisationParts.containerDoor);
        }
        if (!printMode && this.wheelSemiTrailerWidth) {
          const wheelData = {
            wheelTrailerWidth: this.wheelTrailerWidth,
            wheelTrailerHeight: this.wheelTrailerHeight,
            wheelTrailerLength: this.wheelTrailerLength,
            wheelTrailerDistance: this.wheelTrailerDistance,
            wheelSemiTrailerWidth: this.wheelSemiTrailerWidth,
            wheelSemiTrailerHeight: this.wheelSemiTrailerHeight,
            wheelSemiTrailerLength: this.wheelSemiTrailerLength,
            wheelSemiTrailerDistance: this.wheelSemiTrailerDistance,
          };
          this.setVisualisationParts.wheelArch = this.drawWheelArch(wheelData, true);
          renderingContext.scene.add(this.setVisualisationParts.wheelArch);
        }

        if (!printMode && this.coolerSemiTrailerLength) {
          const coolerData = {
            coolerTrailerWidth: this.coolerTrailerWidth,
            coolerTrailerHeight: this.coolerTrailerHeight,
            coolerTrailerLength: this.coolerTrailerLength,
            coolerTrailerFloorHeight: this.coolerTrailerFloorHeight,
            coolerSemiTrailerWidth: this.coolerSemiTrailerWidth,
            coolerSemiTrailerHeight: this.coolerSemiTrailerHeight,
            coolerSemiTrailerLength: this.coolerSemiTrailerLength,
            coolerSemiTrailerFloorHeight: this.coolerSemiTrailerFloorHeight,
          };
          this.setVisualisationParts.cooler = this.drawCooler(coolerData, true);
          renderingContext.scene.add(this.setVisualisationParts.cooler);
        }
        // if (!printMode && this.axis.length > 1) {
        //   this.setVisualisationParts.axis = this.drawAxles();
        //   console.log('drawAxles', this.setVisualisationParts.axis);
        //   renderingContext.scene.add(this.setVisualisationParts.axis);
        // }

        if (
          this.type === 2 &&
          this.view !== 'setLeft' &&
          this.view !== 'left' &&
          this.view !== 'back' &&
          this.gravityShowCenter
        ) {
          this.setVisualisationParts.gravityCenter = this.drawGravityCenter(true);
          renderingContext.scene.add(this.setVisualisationParts.gravityCenter);
        }
      }

      if (printMode) {
        this.visualisationParts.backDoors = this.drawDoors();
        renderingContext.scene.add(this.visualisationParts.backDoors);
      }
    }
  }

  public drawContainerDoor(isSet: boolean): Object3D {
    const isFatLines =
      (!isSet && this.trailerLoadingSide === 'back') ||
      (isSet && this.semiTrailerLoadingSide === 'back');
    const width: number = !isSet ? this.trailerDoorWidth : this.semiTrailerDoorWidth;
    const height: number = !isSet ? this.trailerDoorHeight : this.semiTrailerDoorHeight;
    const position = !isSet ? this.length : this.length + this.gap + this.length1;
    if (!isFatLines) {
      const geometry = new BufferGeometry().setFromPoints([
        new Vector3(0, 0, 0),
        new Vector3(0, height, 0),
        new Vector3(width, height, 0),
        new Vector3(width, 0, 0),
      ]);
      geometry.translate(this.width / 2 - width / 2, 0, 0);
      const line = new Line(geometry, this.lineMaterial);
      line.position.set(0, 0, position);
      return line;
    } else {
      const position = isSet ? this.length1 + this.gap : 0;
      const fatContainerDoor = this.generateLoadingMarkup(width, height, this.markupMaterial, true);
      const setWidth = isSet ? (this.width - width) / 2 + width : (this.width - width) / 2 + width;
      fatContainerDoor.position.set(setWidth, 0, this.length + position);
      return fatContainerDoor;
    }
  }

  public remove(scene: Scene): void {
    scene.remove(this.visualisationParts.sides);
    scene.remove(this.visualisationParts.basis);
    scene.remove(this.visualisationParts.front);
    scene.remove(this.visualisationParts.back);
    scene.remove(this.visualisationParts.axis);
    scene.remove(this.visualisationParts.backDoors);
    scene.remove(this.visualisationParts.measureVertical);
    scene.remove(this.visualisationParts.measureHorizontal);
    scene.remove(this.visualisationParts.varioFloor);
    scene.remove(this.visualisationParts.cooler);
    scene.remove(this.visualisationParts.wheelArch);
    scene.remove(this.visualisationParts.pallet);
    scene.remove(this.visualisationParts.containerDoor);
    scene.remove(this.visualisationParts.gravityCenter);
    if (this.set) {
      scene.remove(this.setVisualisationParts.sides);
      scene.remove(this.setVisualisationParts.basis);
      scene.remove(this.setVisualisationParts.front);
      scene.remove(this.setVisualisationParts.back);
      scene.remove(this.setVisualisationParts.axis);
      scene.remove(this.setVisualisationParts.measureVertical);
      scene.remove(this.setVisualisationParts.measureHorizontal);
      scene.remove(this.setVisualisationParts.varioFloor);
      scene.remove(this.setVisualisationParts.cooler);
      scene.remove(this.setVisualisationParts.wheelArch);
      scene.remove(this.setVisualisationParts.containerDoor);
      scene.remove(this.setVisualisationParts.gravityCenter);
    }
  }

  public times100(): void {
    this.length = _.round(this.length * 100, 2);
    this.width = _.round(this.width * 100, 2);
    this.height = _.round(this.height * 100, 2);
    this.length1 = _.round(this.length1 * 100, 2);
    this.width1 = _.round(this.width1 * 100, 2);
    this.height1 = _.round(this.height1 * 100, 2);
    this.dimensionsOfCabin.length = _.round(this.dimensionsOfCabin.length * 100, 2);
    this.dimensionsOfCabin.width = _.round(this.dimensionsOfCabin.width * 100, 2);
    this.dimensionsOfCabin.height = _.round(this.dimensionsOfCabin.height * 100, 2);
    this.dimensionsOfCabin.gap = _.round(this.dimensionsOfCabin.gap * 100, 2);
    this.dimensionsOfCabin.height1 = _.round(this.dimensionsOfCabin.height1 * 100, 2);
    this.dimensionsOfCabin.height2 = _.round(this.dimensionsOfCabin.height2 * 100, 2);
    this.dimensionsOfCabin.length1 = _.round(this.dimensionsOfCabin.length1 * 100, 2);
    this.palletMaxHeight = this.palletMaxHeight ? _.round(this.palletMaxHeight * 100, 2) : 0;
    this.palletHeight = this.palletHeight ? _.round(this.palletHeight * 100, 2) : 0;
    if (this.varioTrailerFloorHeight) {
      this.varioTrailerFloorHeight = _.round(this.varioTrailerFloorHeight * 100, 2);
      this.varioTrailerFloorThickness = _.round(this.varioTrailerFloorThickness * 100, 2);
      this.varioTrailerFloorCapacity = _.round(this.varioTrailerFloorCapacity * 100, 2);
    }
    if (this.varioSemiTrailerFloorHeight) {
      this.varioSemiTrailerFloorHeight = _.round(this.varioSemiTrailerFloorHeight * 100, 2);
      this.varioSemiTrailerFloorThickness = _.round(this.varioSemiTrailerFloorThickness * 100, 2);
      this.varioSemiTrailerFloorCapacity = _.round(this.varioSemiTrailerFloorCapacity * 100, 2);
    }
    if (this.coolerTrailerLength) {
      this.coolerTrailerLength = _.round(this.coolerTrailerLength * 100, 2);
      this.coolerTrailerHeight = _.round(this.coolerTrailerHeight * 100, 2);
      this.coolerTrailerWidth = _.round(this.coolerTrailerWidth * 100, 2);
      this.coolerTrailerFloorHeight = _.round(this.coolerTrailerFloorHeight * 100, 2);
    }
    if (this.coolerSemiTrailerLength) {
      this.coolerSemiTrailerLength = _.round(this.coolerSemiTrailerLength * 100, 2);
      this.coolerSemiTrailerHeight = _.round(this.coolerSemiTrailerHeight * 100, 2);
      this.coolerSemiTrailerWidth = _.round(this.coolerSemiTrailerWidth * 100, 2);
      this.coolerSemiTrailerFloorHeight = _.round(this.coolerSemiTrailerFloorHeight * 100, 2);
    }
    if (this.wheelTrailerLength) {
      this.wheelTrailerLength = _.round(this.wheelTrailerLength * 100, 2);
      this.wheelTrailerHeight = _.round(this.wheelTrailerHeight * 100, 2);
      this.wheelTrailerWidth = _.round(this.wheelTrailerWidth * 100, 2);
      this.wheelTrailerDistance = _.round(this.wheelTrailerDistance * 100, 2);
    }
    if (this.wheelSemiTrailerLength) {
      this.wheelSemiTrailerLength = _.round(this.wheelSemiTrailerLength * 100, 2);
      this.wheelSemiTrailerHeight = _.round(this.wheelSemiTrailerHeight * 100, 2);
      this.wheelSemiTrailerWidth = _.round(this.wheelSemiTrailerWidth * 100, 2);
      this.wheelSemiTrailerDistance = _.round(this.wheelSemiTrailerDistance * 100, 2);
    }
    if (this.airLengthA && this.airWidth && this.airBaseB && this.airHeightC && this.airHeightD) {
      this.airLengthA = _.round(this.airLengthA * 100, 2);
      this.airWidth = _.round(this.airWidth * 100, 2);
      this.airBaseB = _.round(this.airBaseB * 100, 2);
      this.airHeightC = _.round(this.airHeightC * 100, 2);
      this.airHeightD = _.round(this.airHeightD * 100, 2);
    }
    if (this.trailerDoorHeight && this.trailerDoorWidth) {
      this.trailerDoorHeight = _.round(this.trailerDoorHeight * 100, 2);
      this.trailerDoorWidth = _.round(this.trailerDoorWidth * 100, 2);
    }
    if (this.semiTrailerDoorHeight && this.semiTrailerDoorWidth) {
      this.semiTrailerDoorHeight = _.round(this.semiTrailerDoorHeight * 100, 2);
      this.semiTrailerDoorWidth = _.round(this.semiTrailerDoorWidth * 100, 2);
    }
  }

  public hideFront(): void {
    if (this.visualisationParts.front) {
      this.visualisationParts.front.visible = false;
      if (this.set) {
        this.setVisualisationParts.front.visible = false;
      }
    }
  }

  public showFront(): void {
    if (this.visualisationParts.front) {
      this.visualisationParts.front.visible = true;
      if (this.set) {
        this.setVisualisationParts.front.visible = true;
      }
    }
  }

public hideAxles(): void {
  if (this.visualisationParts?.axis) {
    this.visualisationParts.axis.visible = false;
  }
  if (this.set && this.setVisualisationParts?.axis) {
    this.setVisualisationParts.axis.visible = false;
  }
}

  public hideBackDoors(): void {
    if (this.visualisationParts.backDoors) {
      this.visualisationParts.backDoors.visible = false;
    }
  }

  public showBackDoors(): void {
    if (this.visualisationParts.backDoors) {
      this.visualisationParts.backDoors.visible = true;
    }
  }

  public hideMeasure(scene: Scene): void {
    scene.remove(this.visualisationParts.measureVertical);
    scene.remove(this.visualisationParts.measureHorizontal);
    if (this.set) {
      scene.remove(this.setVisualisationParts.measureVertical);
      scene.remove(this.setVisualisationParts.measureHorizontal);
    }
  }

  public showMeasure(scene: Scene): void {
    this.visualisationParts.measureVertical = this.drawMeasureVertical();
    scene.add(this.visualisationParts.measureVertical);
    this.visualisationParts.measureHorizontal = this.drawMeasureHorizontal();
    scene.add(this.visualisationParts.measureHorizontal);
    if (this.airType !== 1) {
      this.visualisationParts.measureHeight = this.drawMeasureHeight();
      scene.add(this.visualisationParts.measureHeight);
    }
    if (this.set) {
      this.setVisualisationParts.measureVertical = this.drawSetMeasureVertical();
      scene.add(this.setVisualisationParts.measureVertical);
      this.setVisualisationParts.measureHorizontal = this.drawSetMeasureHorizontal();
      scene.add(this.setVisualisationParts.measureHorizontal);
      if (this.type !== 3) {
        this.setVisualisationParts.measureHeight = this.drawSetMeasureHeight();
        scene.add(this.setVisualisationParts.measureHeight);
      }
    }
  }

private compareAxis(a: Axle[], b: Axle[]): boolean {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }
  const sortedA = [...a].sort((axle1, axle2) =>
    axle1.emptySpaceLoad - axle2.emptySpaceLoad);
  const sortedB = [...b].sort((axle1, axle2) =>
    axle1.emptySpaceLoad - axle2.emptySpaceLoad);
  return sortedA.every((axleA, index) => {
    const axleB = sortedB[index];
    return (
      axleA.addedLoad === axleB.addedLoad &&
      axleA.maxLoad === axleB.maxLoad &&
      axleA.emptySpaceLoad === axleB.emptySpaceLoad &&
      axleA.distanceFromSpaceFront === axleB.distanceFromSpaceFront
    );
  });
}

  public isLoadingSpaceSame(loadingSpace: LoadingSpace): boolean {
    return (
      this.type === loadingSpace.type &&
      this.length === loadingSpace.length &&
      this.width === loadingSpace.width &&
      this.height === loadingSpace.height &&
      this.limit === loadingSpace.limit &&
      ((!this.set && !loadingSpace.set) ||
        (this.set &&
          loadingSpace.set &&
          this.length1 === loadingSpace.length1 &&
          this.width1 === loadingSpace.width1 &&
          this.height1 === loadingSpace.height1 &&
          this.compareAxis(this.axis, loadingSpace.axis))) &&
      this.compareAxis(this.axis, loadingSpace.axis) &&
      this.trailerLoadingSide === loadingSpace.trailerLoadingSide &&
      this.semiTrailerLoadingSide === loadingSpace.semiTrailerLoadingSide &&
      this.varioTrailerFloorHeight === loadingSpace.varioTrailerFloorHeight &&
      this.varioSemiTrailerFloorHeight === loadingSpace.varioSemiTrailerFloorHeight &&
      this.varioTrailerFloorThickness === loadingSpace.varioTrailerFloorThickness &&
      this.varioSemiTrailerFloorThickness === loadingSpace.varioSemiTrailerFloorThickness &&
      this.varioTrailerFloorCapacity === loadingSpace.varioTrailerFloorCapacity &&
      this.varioSemiTrailerFloorCapacity === loadingSpace.varioSemiTrailerFloorCapacity &&
      this.coolerTrailerWidth === loadingSpace.coolerTrailerWidth &&
      this.coolerTrailerHeight === loadingSpace.coolerTrailerHeight &&
      this.coolerTrailerLength === loadingSpace.coolerTrailerLength &&
      this.coolerTrailerFloorHeight === loadingSpace.coolerTrailerFloorHeight &&
      this.coolerSemiTrailerWidth === loadingSpace.coolerSemiTrailerWidth &&
      this.coolerSemiTrailerHeight === loadingSpace.coolerSemiTrailerHeight &&
      this.coolerSemiTrailerLength === loadingSpace.coolerSemiTrailerLength &&
      this.coolerSemiTrailerFloorHeight === loadingSpace.coolerSemiTrailerFloorHeight &&
      this.wheelTrailerWidth === loadingSpace.wheelTrailerWidth &&
      this.wheelTrailerHeight === loadingSpace.wheelTrailerHeight &&
      this.wheelTrailerLength === loadingSpace.wheelTrailerLength &&
      this.wheelTrailerDistance === loadingSpace.wheelTrailerDistance &&
      this.wheelSemiTrailerWidth === loadingSpace.wheelSemiTrailerWidth &&
      this.wheelSemiTrailerHeight === loadingSpace.wheelSemiTrailerHeight &&
      this.wheelSemiTrailerLength === loadingSpace.wheelSemiTrailerLength &&
      this.wheelSemiTrailerDistance === loadingSpace.wheelSemiTrailerDistance &&
      this.unit === loadingSpace.unit &&
      this.airType === loadingSpace.airType &&
      this.airLengthA === loadingSpace.airLengthA &&
      this.airWidth === loadingSpace.airWidth &&
      this.airBaseB === loadingSpace.airBaseB &&
      this.airHeightC === loadingSpace.airHeightC &&
      this.airHeightD === loadingSpace.airHeightD &&
      this.airLoadCapacity === loadingSpace.airLoadCapacity &&
      this.trailerLoadingSide === loadingSpace.trailerLoadingSide &&
      this.semiTrailerLoadingSide === loadingSpace.semiTrailerLoadingSide &&
      this.airQuantity === loadingSpace.airQuantity &&
      this.trailerDoorWidth === loadingSpace.trailerDoorWidth &&
      this.trailerDoorHeight === loadingSpace.trailerDoorHeight &&
      this.semiTrailerDoorWidth === loadingSpace.semiTrailerDoorWidth &&
      this.semiTrailerDoorHeight === loadingSpace.semiTrailerDoorHeight &&
      this.view === loadingSpace.view &&
      this.palletMaxHeight === loadingSpace.palletMaxHeight &&
      this.palletHeight === loadingSpace.palletHeight &&
      this.gravityShowCenter === loadingSpace.gravityShowCenter &&
      this.gravitySemiTrailerLengthTolerance === loadingSpace.gravitySemiTrailerLengthTolerance &&
      this.gravitySemiTrailerWidthTolerance === loadingSpace.gravitySemiTrailerWidthTolerance &&
      this.gravityTrailerLengthTolerance === loadingSpace.gravityTrailerLengthTolerance &&
      this.gravityTrailerWidthTolerance === loadingSpace.gravityTrailerWidthTolerance &&
      this.trailerContainerWeight === loadingSpace.trailerContainerWeight &&
      this.semiTrailerContainerWeight === loadingSpace.semiTrailerContainerWeight &&
      this.trailerCOGX === loadingSpace.trailerCOGX &&
      this.trailerCOGY === loadingSpace.trailerCOGY &&
      this.trailerCOGZ === loadingSpace.trailerCOGZ &&
      this.semiTrailerCOGX === loadingSpace.semiTrailerCOGX &&
      this.semiTrailerCOGY === loadingSpace.semiTrailerCOGY &&
      this.semiTrailerCOGZ === loadingSpace.semiTrailerCOGZ
    );
  }

  public isLoadingSpaceSameWithoutAxis(loadingSpace: LoadingSpace): boolean {
    return (
      this.type === loadingSpace.type &&
      this.length === loadingSpace.length &&
      this.width === loadingSpace.width &&
      this.height === loadingSpace.height &&
      this.trailerLoadingSide === loadingSpace.trailerLoadingSide &&
      this.semiTrailerLoadingSide === loadingSpace.semiTrailerLoadingSide &&
      ((!this.set && !loadingSpace.set) ||
        (this.set &&
          loadingSpace.set &&
          this.length1 === loadingSpace.length1 &&
          this.width1 === loadingSpace.width1 &&
          this.height1 === loadingSpace.height1))
    );
  }

  public isLoadingSpaceNameSame(loadingSpace: LoadingSpace): boolean {
    return loadingSpace.name === this.name;
  }

  public getLoadingSpaceLength(): number {
    return this.set ? this.length + this.length1 + this.gap : this.length;
  }

  public getLoadingSpaceLengthWithCabinAndDoors(): number {
    return this.set
      ? this.length +
          this.length1 +
          this.gap +
          (this.dimensionsOfCabin.length + this.dimensionsOfCabin.gap) * 2
      : this.length + (this.dimensionsOfCabin.length + this.dimensionsOfCabin.gap) * 2;
  }

  public getBaseCenterPosition(): Vector3 {
    let position: Position;
    if (!this.set) {
      position = {
        x: this.width / 2,
        y: 0,
        z: this.length / 2,
      };
    } else {
      position = {
        x: this.width / 2,
        y: 0,
        z: (this.length + this.gap + this.length1) / 2,
      };
    }
    return position as Vector3;
  }

  public hasPosition(position: Vector3): boolean {
    return this.isInFirstPart(position) || this.isInSecondPart(position);
  }

  public isInFirstPart(position: Vector3): boolean {
    const firstPartPosition: Vector3 = this.firstPartPosition();
    return (
      position.x >= firstPartPosition.x &&
      position.x < firstPartPosition.x + this.width &&
      position.z >= firstPartPosition.z &&
      position.z < firstPartPosition.z + this.length &&
      position.y >= firstPartPosition.y &&
      position.y < firstPartPosition.y + this.height
    );
  }

  public isInSecondPart(position: Vector3): boolean {
    if (!this.set) {
      return false;
    }
    const secondPartPosition: Vector3 = this.secondPartPosition();
    return (
      position.x >= secondPartPosition.x &&
      position.x < secondPartPosition.x + this.width1 &&
      position.z >= secondPartPosition.z &&
      position.z < secondPartPosition.z + this.length1 &&
      position.y >= secondPartPosition.y &&
      position.y < secondPartPosition.y + this.height1
    );
  }

  protected drawCooler(coolerData: CoolerData, isSet = false): Object3D {
    const group = new Group();
    group.name = !isSet ? 'Cooler' : 'CoolerSet';
    const width = !isSet ? coolerData.coolerTrailerWidth : coolerData.coolerSemiTrailerWidth;
    const height = !isSet ? coolerData.coolerTrailerHeight : coolerData.coolerSemiTrailerHeight;
    const length = !isSet ? coolerData.coolerTrailerLength : coolerData.coolerSemiTrailerLength;
    const floorHeight = !isSet
      ? coolerData.coolerTrailerFloorHeight
      : coolerData.coolerSemiTrailerFloorHeight;
    const coolerGeometry = new BoxGeometry(width, height, length).translate(
      0,
      height / 2,
      length / 2,
    );
    const position = !isSet ? 0 : this.length + this.gap;
    const coolerMesh = new Mesh(coolerGeometry, this.meshTransparentMaterial);
    coolerMesh.name = !isSet ? 'CoolerMesh' : 'CoolerMeshSet';
    coolerMesh.position.set(this.width / 2, floorHeight, position);
    const edgesGeometry = new EdgesGeometry(coolerGeometry);
    const lineMaterial =
      (this.view === 'setLeft' && !isSet) || (this.view === 'left' && isSet)
        ? this.lineGreyMaterial
        : this.lineMaterial;
    const cooler = new LineSegments(edgesGeometry, lineMaterial);
    cooler.name = !isSet ? 'CoolerLine' : 'CoolerLineSet';
    cooler.position.set(this.width / 2, floorHeight, position);
    group.add(cooler);
    group.add(coolerMesh);
    return group;
  }

  protected drawWheelArch(wheelData: WheelData, isSet = false): Object3D {
    const width = !isSet ? wheelData.wheelTrailerWidth : wheelData.wheelSemiTrailerWidth;
    const height = !isSet ? wheelData.wheelTrailerHeight : wheelData.wheelSemiTrailerHeight;
    const length = !isSet ? wheelData.wheelTrailerLength : wheelData.wheelSemiTrailerLength;
    const doorDistance = !isSet
      ? wheelData.wheelTrailerDistance
      : wheelData.wheelSemiTrailerDistance;

    const positionZ = !isSet
      ? this.length - doorDistance
      : this.length + this.gap + this.length1 - doorDistance;
    const positionX = !isSet ? 0 : -(this.width1 - this.width) / 2;
    const wheel1Geometry = new BoxGeometry(width, height, length).translate(
      width / 2,
      height / 2,
      -length / 2,
    );
    const wheel1EdgesGeometry = new EdgesGeometry(wheel1Geometry);
    const lineMaterial =
      (this.view === 'setLeft' && !isSet) || (this.view === 'left' && isSet)
        ? this.lineGreyMaterial
        : this.lineMaterial;
    const wheel1 = new LineSegments(wheel1EdgesGeometry, lineMaterial);
    wheel1.position.set(positionX, 0, positionZ);

    const wheel2Geometry = new BoxGeometry(width, height, length).translate(
      width / 2,
      height / 2,
      -length / 2,
    );
    const wheel2EdgesGeometry = new EdgesGeometry(wheel2Geometry);
    const wheel2 = new LineSegments(wheel2EdgesGeometry, lineMaterial);
    wheel2.position.set(this.width - width - positionX, 0, positionZ);
    const wheelMesh = new Mesh(wheel1Geometry, this.meshTransparentMaterial);
    const offset = 0.005;
    wheelMesh.position.set(positionX + offset, offset, positionZ + offset);
    wheelMesh.name = !isSet ? 'WheelMesh' : 'WheelMeshSet';
    const wheelMesh2 = new Mesh(wheel2Geometry, this.meshTransparentMaterial);
    wheelMesh2.position.set(this.width - width + offset - positionX, offset, positionZ + offset);
    wheelMesh2.name = !isSet ? 'WheelMesh' : 'WheelMeshSet';
    const wheelArchGroup = new Group();
    wheelArchGroup.name = 'WheelArch';
    wheelArchGroup.add(wheelMesh);
    wheelArchGroup.add(wheelMesh2);
    wheelArchGroup.add(wheel1);
    wheelArchGroup.add(wheel2);
    return wheelArchGroup;
  }

  protected drawVarioFloor(
    floorHeight = 0,
    floorThicknes: number,
    isSet = false,
  ): Object3D {
    const width = !isSet ? this.width : this.width1;
    const length = !isSet ? this.length : this.length1;
    const geometry = new PlaneGeometry(width, length);
    geometry.translate(width / 2, length / 2, 0);
    const plane = new Mesh(geometry, this.basisMaterial);
    plane.name = !isSet ? 'VarioFloor' : 'VarioFloorSet';
    const positionZ = !isSet ? 0 : this.length + this.gap;
    const positionX = !isSet ? 0 : -(this.width1 - this.width) / 2;
    const frame = new EdgesGeometry(geometry);
    const lineMaterial =
      (this.view === 'setLeft' && !isSet) || (this.view === 'left' && isSet)
        ? this.lineGreyMaterial
        : this.lineMaterial;
    const planeFrame = new LineSegments(frame, lineMaterial);
    planeFrame.position.set(positionX, floorHeight, positionZ);
    planeFrame.rotation.set(Math.PI / 2, 0, 0);
    plane.position.set(positionX, floorHeight, positionZ);
    plane.rotation.set(Math.PI / 2, 0, 0);
    const floorBaseGeometry = new BoxGeometry(width, floorThicknes, length).translate(
      width / 2,
      -floorThicknes / 2,
      length / 2,
    );
    const edgesFloorBaseGeometry = new EdgesGeometry(floorBaseGeometry);
    const floorBase = new LineSegments(edgesFloorBaseGeometry, lineMaterial);
    floorBase.position.set(positionX, floorHeight, positionZ);
    const floorGroup = new Group();
    floorGroup.name = !isSet ? 'VarioFloor' : 'VarioFloorSet';
    floorGroup.add(plane);
    floorGroup.add(planeFrame);
    floorGroup.add(floorBase);
    return floorGroup;
  }

  protected drawBasis(
    yHeight = 0,
    z = 0,
    width = this.width,
    length = this.length,
    height = this.height,
  ): Object3D {
    const group = new Group();
    group.name = 'Basis';
    const geometry = new PlaneGeometry(width, length);
    geometry.translate(width / 2, length / 2, 0);
    const plane = new Mesh(geometry, this.basisMaterial);
    // Hack that solves z-fighting problem
    plane.position.set(0, -height * 0.001 + yHeight, z);
    plane.rotation.set(Math.PI / 2, 0, 0);
    plane.name = 'Basis';
    group.add(plane);

    const ceiling = new Mesh(geometry);
    ceiling.visible = false;
    ceiling.position.set(0, this.height, z);
    ceiling.rotation.set(Math.PI / 2, 0, 0);
    ceiling.name = 'Ceiling';
    group.add(ceiling);

    if (this.type === 1 || this.type === 2) {
      const backGeometry = new PlaneGeometry(width, height);
      backGeometry.translate(width / 2, height / 2, 0);
      const backMesh = new Mesh(backGeometry, this.meshTransparentMaterial);
      backMesh.position.set(0, 0, length);
      backMesh.rotation.set(0, 0, 0);
      backMesh.name = 'backSide';
      group.add(backMesh);
    }
    return group;
  }

  protected drawSetBasis(): Object3D {
    const geometry = new PlaneGeometry(this.width1, this.length1);
    geometry.translate(this.width1 / 2, this.length1 / 2, 0);
    const plane = new Mesh(geometry, this.basisMaterial);
    // Hack that solves z-fighting problem
    plane.position.set(
      (this.width - this.width1) / 2,
      -this.height1 * 0.001,
      this.length + this.gap,
    );
    plane.rotation.set(Math.PI / 2, 0, 0);
    plane.name = 'SetBasis';
    return plane;
  }

  protected drawDoors(): Group {
    const group = new Group();
    group.name = 'Doors';
    const startDrawZPosition = this.set ? this.length + this.length1 + this.gap : this.length;
    const geometry = new CircleGeometry(this.width / 2, 128, 0, 1.575);
    const firstDoor = new Line(geometry, this.lineMaterial);
    firstDoor.position.set(0, 0, startDrawZPosition);
    firstDoor.rotation.set(Math.PI / 2, Math.PI, Math.PI / 2);
    group.add(firstDoor);
    const secondDoor = new Line(geometry, this.lineMaterial);
    secondDoor.position.set(this.width, 0, startDrawZPosition);
    secondDoor.rotation.set(Math.PI / 2, 0, Math.PI / 2);
    group.add(secondDoor);
    const WIDTH_LINE = this.unit === 'cm' ? 500 : 200;
    const lineGeometry = new PlaneGeometry(WIDTH_LINE, this.width / 2);
    lineGeometry.translate(WIDTH_LINE / 2, this.width / 4, 0);
    const line = new Mesh(lineGeometry, this.meshMaterial);
    line.position.set(0, 0, startDrawZPosition);
    line.rotation.set(Math.PI / 2, 0, 0);
    group.add(line);
    const line1Geometry = new PlaneGeometry(WIDTH_LINE, this.width / 2);
    line1Geometry.translate(-WIDTH_LINE / 2, this.width / 4, 0);
    const line1 = new Mesh(line1Geometry, this.meshMaterial);
    line1.position.set(this.width, 0, startDrawZPosition);
    line1.rotation.set(Math.PI / 2, 0, 0);
    group.add(line1);
    this.dimensionsOfCabin.length = this.width / 2;
    return group;
  }

  protected drawPallet(): Object3D {
    const group = new Group();

    const palletBaseHeight = this.palletHeight;
    const palletBaseGeometry = new BoxGeometry(
      this.width,
      palletBaseHeight,
      this.length,
    ).translate(0, (palletBaseHeight - 0.0001) / 2, this.length / 2);
    const edgesGeometry = new EdgesGeometry(palletBaseGeometry);
    const palletBase = new LineSegments(edgesGeometry, this.basisMaterial);
    palletBase.position.set(this.width / 2, 0, 0);
    palletBase.renderOrder = 1;
    const palletBaseMesh = new Mesh(palletBaseGeometry, this.basisMaterial);
    palletBaseMesh.position.set(this.width / 2, 0, 0);
    if (this.palletMaxHeight) {
      const palletHeight = this.palletMaxHeight - this.palletHeight;
      const palletGeometry = new BoxGeometry(this.width, palletHeight, this.length).translate(
        0,
        palletHeight / 2,
        this.length / 2,
      );
      const palletEdgesGeometry = new EdgesGeometry(palletGeometry);
      const pallet = new LineSegments(palletEdgesGeometry, this.lineMaterial);
      pallet.position.set(this.width / 2, palletBaseHeight, 0);
      pallet.renderOrder = 2;
      group.add(palletBaseMesh);
      group.add(pallet);
    }
    group.add(palletBase);

    const leftGeometry = new PlaneGeometry(this.width, this.height);
    leftGeometry.translate(this.width / 2, this.height / 2, 0);
    const leftMesh = new Mesh(leftGeometry, this.meshTransparentMaterial);
    leftMesh.position.set(0.5, 0, 0);
    leftMesh.rotation.set(0, 0, 0);
    leftMesh.name = 'leftSide';
    group.add(leftMesh);

    const rightGeometry = new PlaneGeometry(this.width, this.height);
    rightGeometry.translate(this.width / 2, this.height / 2, 0);
    const rightMesh = new Mesh(rightGeometry, this.meshTransparentMaterial);
    rightMesh.position.set(0.5, 0, this.length);
    rightMesh.rotation.set(0, 0, 0);
    rightMesh.name = 'rightSide';
    group.add(rightMesh);

    const frontGeometry = new PlaneGeometry(this.length, this.height);
    frontGeometry.translate(this.length / 2, this.height / 2, 0);
    const frontMesh = new Mesh(frontGeometry, this.meshTransparentMaterial);
    frontMesh.position.set(0, 0, this.length);
    frontMesh.rotation.set(0, Math.PI / 2, 0);
    frontMesh.name = 'frontSide';
    group.add(frontMesh);

    const backGeometry = new PlaneGeometry(this.length, this.height);
    backGeometry.translate(this.length / 2, this.height / 2, 0);
    const backMesh = new Mesh(backGeometry, this.meshTransparentMaterial);
    backMesh.position.set(this.width, 0, this.length);
    backMesh.rotation.set(0, Math.PI / 2, 0);
    backMesh.name = 'backSide';
    group.add(backMesh);

    return group;
  }

  protected drawBack(): Object3D {
    const geometry = new BufferGeometry().setFromPoints([
      new Vector3(this.width, this.height, 0),
      new Vector3(this.width, 0, 0),
      new Vector3(0, 0, 0),
      new Vector3(0, this.height, 0),
      new Vector3(this.width, this.height, 0),
    ]);
    const line = new Line(geometry, this.lineMaterial);
    line.position.set(0, 0, this.length);
    line.name = 'Back';
    return line;
  }

  protected drawSetBack(): Object3D {
    const geometry = new BufferGeometry().setFromPoints([
      new Vector3(this.width1, this.height1, 0),
      new Vector3(this.width1, 0, 0),
      new Vector3(0, 0, 0),
      new Vector3(0, this.height1, 0),
      new Vector3(this.width1, this.height1, 0),
    ]);
    const line = new Line(geometry, this.lineMaterial);
    line.position.set((this.width - this.width1) / 2, 0, this.length + this.length1 + this.gap);
    line.name = 'SetBack';
    return line;
  }

  protected drawAxis(coneZPosition: number, color: string): Object3D {
    const group: Group = new Group();
    group.name = 'Axis';
    const coneRadius = 0.05 * this.width;
    const coneHeight = 0.15 * this.width;
    const coneMaterial = new MeshBasicMaterial();
    coneMaterial.color.set(color);
    const coneGeometry = new ConeGeometry(coneRadius, coneHeight, 64);
    const cone1 = new Mesh(coneGeometry, coneMaterial.clone());
    cone1.name = 'Axis';
    const cone2 = new Mesh(coneGeometry, coneMaterial.clone());
    cone2.name = 'Axis';
    cone1.position.set(-coneHeight / 2, 0, coneZPosition);
    cone1.rotation.set(-Math.PI / 2, 0, -Math.PI / 2);
    cone2.position.set(coneHeight / 2 + this.width, 0, coneZPosition);
    cone2.rotation.set(Math.PI / 2, 0, Math.PI / 2);
    cone1.castShadow = true;
    cone2.castShadow = true;
    group.add(cone1);
    group.add(cone2);

    const ringHeight = 0.005 * this.width;
    const ringGeometry = new RingGeometry(
      coneRadius - ringHeight,
      coneRadius + ringHeight,
      64,
    );
    const ring1 = new Mesh(ringGeometry, this.lineMaterial);
    const ring2 = new Mesh(ringGeometry, this.lineMaterial);
    ring1.position.set(-coneHeight, 0, coneZPosition);
    ring2.position.set(coneHeight + this.width, 0, coneZPosition);
    ring1.rotation.set(0, Math.PI / 2, 0);
    ring2.rotation.set(0, Math.PI / 2, 0);
    group.add(ring1);
    group.add(ring2);

    const lineGeometry = new BufferGeometry().setFromPoints([
      new Vector3(-this.width / 2, 0, 0),
      new Vector3(this.width / 2, 0, 0),
    ]);
    const line = new Line(lineGeometry, this.lineMaterial);
    line.position.set(this.width / 2, 0, coneZPosition);

    group.add(line);
    return group;
  }

  protected drawAxles(): Object3D {
    const group: Group = new Group();
    group.name = 'Axles';
    this.axis.forEach((axi: Axle): void => {
      group.add(this.drawAxi(axi));
    });
    return group;
  }

  protected drawAxi(axi: Axle): Object3D {
    const setStart: number = this.length + this.gap;
    const rawDistance: number | null | undefined =
      axi.distanceFromSpaceFront !== undefined ? axi.distanceFromSpaceFront : axi.position?.z;
    const distanceFromSpaceFront: number = (rawDistance !== null && rawDistance !== undefined && !isNaN(rawDistance as number)) ? rawDistance : 0;
    const axiPosition: number =
      axi.loadingSpacePart === 2 ? setStart / 100 + distanceFromSpaceFront : distanceFromSpaceFront;
    const coneZPosition: number = _.round(axiPosition * 100, 2);
    const color: string = this.colorAxi(axi);
    return this.drawAxis(coneZPosition, color);
  }

  protected colorAxi(axi: Axle): string {
    const color1 = { red: 78, green: 179, blue: 255 };
    const color2 = { red: 255, green: 139, blue: 139 };
    let fade = axi.addedLoad / axi.maxLoad;
    if (fade > 1) {
      fade = 1;
    } else if (fade < 0) {
      fade = 0;
    } else if (isNaN(fade)) {
      fade = 0;
    }

    const diffRed = color2.red - color1.red;
    const diffGreen = color2.green - color1.green;
    const diffBlue = color2.blue - color1.blue;

    const gradient = {
      red: parseInt(Math.floor(color1.red + diffRed * fade).toString(), 10),
      green: parseInt(Math.floor(color1.green + diffGreen * fade).toString(), 10),
      blue: parseInt(Math.floor(color1.blue + diffBlue * fade).toString(), 10),
    };

    return `rgb(${gradient.red},${gradient.green},${gradient.blue})`;
  }

  protected drawGravityCenter(isSet = false): Object3D {
    const width = isSet ? this.width1 : this.width;
    const height = isSet ? this.height1 : this.height;
    const length = isSet ? this.length1 : this.length;

    const group = new Group();
    group.name = 'GravityCenter';

    const greenLineGeo = new BufferGeometry().setFromPoints([
      new Vector3(width / 2, height, 0),
      new Vector3(width / 2, height, length),
    ]);
    const greenLine = new Line(greenLineGeo, this.lineGreenTransparentMaterial);
    const offset = 10;
    const redLineGeo = new BufferGeometry().setFromPoints([
      new Vector3(0, height + offset, length / 2),
      new Vector3(width, height + offset, length / 2),
    ]);
    const redLine = new Line(redLineGeo, this.lineRedTransparentMaterial);
    group.add(greenLine);
    group.add(redLine);
    let greenLine2Points: Vector3[];
    if (this.type === 4) {
      const bottomOffset = this.airType === 1 ? (this.width - this.airBaseB) / 2 : 0;
      greenLine2Points = [
        new Vector3(width / 2, offset, bottomOffset),
        new Vector3(width / 2, offset, this.airBaseB + bottomOffset),
      ];
    } else {
      greenLine2Points = [
        new Vector3(width / 2, offset, 0),
        new Vector3(width / 2, offset, length),
      ];
    }
    const greenLine2Geo = new BufferGeometry().setFromPoints(greenLine2Points);
    const greenLine2 = new Line(greenLine2Geo, this.lineGreenTransparentMaterial);
    let redLine2Points: Vector3[];
    if (this.type === 4) {
      const bottomOffset = this.airType === 1 ? 0 : -(length / 2) + this.airBaseB / 2;
      redLine2Points = [
        new Vector3(0, offset, length / 2 + bottomOffset),
        new Vector3(width, offset, length / 2 + bottomOffset),
      ];
    } else {
      redLine2Points = [
        new Vector3(0, offset, length / 2),
        new Vector3(width, offset, length / 2),
      ];
    }
    const redLine2Geo = new BufferGeometry().setFromPoints(redLine2Points);
    const redLine2 = new Line(redLine2Geo, this.lineRedTransparentMaterial);
    group.add(greenLine2);
    group.add(redLine2);
    if (isSet) {
      const xPosition = (this.width1 - this.width) / 2;
      group.position.set(-xPosition, 0, this.length + this.gap);
    }
    const gravityArrow = this.drawGravityArrow(isSet);
    group.add(gravityArrow);

    const gravityRectangle = this.drawGravityRectangle(isSet);
    group.add(gravityRectangle);

    return group;
  }

  protected drawGravityRectangle(isSet = false): Object3D {
    const group = new Group();
    group.name = 'gravityRectangle';
    const material = this.markupMaterial;

    const widthTolerance = isSet
      ? this.gravitySemiTrailerWidthTolerance * 100 * 2
      : this.gravityTrailerWidthTolerance * 100 * 2;
    const lengthTolerance = isSet
      ? this.gravitySemiTrailerLengthTolerance * 100 * 2
      : this.gravityTrailerLengthTolerance * 100 * 2;

    if (widthTolerance && lengthTolerance) {
      const width = isSet ? this.width1 : this.width;
      const length = isSet ? this.length1 : this.length;
      const lineWidth = this.unit !== 'lb' ? 200 : 200 / 2.4;
      const lineDepth = lineWidth;

      const boxLeftGeometry = new BoxGeometry(widthTolerance, lineWidth, lineDepth);
      boxLeftGeometry.translate(widthTolerance / 2, 0, 0);
      const boxLeftMesh = new Mesh(boxLeftGeometry, material);
      boxLeftMesh.position.set(width / 2 - widthTolerance / 2, 0, length / 2 - lengthTolerance / 2);
      group.add(boxLeftMesh);

      const boxTopGeometry = new BoxGeometry(lineDepth, lineWidth, lengthTolerance);
      boxTopGeometry.translate(0, 0, lengthTolerance / 2);
      const boxTopMesh = new Mesh(boxTopGeometry, material);
      boxTopMesh.position.set(width / 2 + widthTolerance / 2, 0, length / 2 - lengthTolerance / 2);
      group.add(boxTopMesh);

      const boxRightGeometry = boxLeftGeometry;
      const boxRightMesh = new Mesh(boxRightGeometry, material);
      boxRightMesh.position.set(
        width / 2 - widthTolerance / 2,
        0,
        length / 2 + lengthTolerance / 2,
      );
      group.add(boxRightMesh);

      const boxBottomGeometry = boxTopGeometry;
      const boxBottomMesh = new Mesh(boxBottomGeometry, material);
      boxBottomMesh.position.set(
        width / 2 - widthTolerance / 2,
        0,
        length / 2 - lengthTolerance / 2,
      );
      group.add(boxBottomMesh);
    }
    const isAirContainerTypeSingle = this.type === 4 && this.airType === 2;
    if (isAirContainerTypeSingle) {
      group.translateZ(-this.width / 2 + this.airBaseB / 2);
    }

    return group;
  }

  protected drawGravityArrow(isSet = false): Object3D {
    const gravityArrowGroup = new Group();
    gravityArrowGroup.name = 'gravityArrowGroup';
    const width = isSet ? this.width1 : this.width;
    const height = isSet ? this.height1 : this.height;
    const length = isSet ? this.length1 : this.length;
    const isCog = !isSet ? this.trailerCOGX !== null : this.semiTrailerCOGX !== null;
    const cogX = isSet ? this.semiTrailerCOGX * 100 : this.trailerCOGX * 100;
    const cogY = isSet ? this.semiTrailerCOGY * 100 : this.trailerCOGY * 100;
    const cogZ = isSet ? this.semiTrailerCOGZ * 100 : this.trailerCOGZ * 100;
    const positionX = isCog ? cogX : width / 2;
    const positionZ = isCog ? cogZ : length / 2;
    const cross1Geo = new BoxGeometry(2000, 10, 200);
    const cross1Mesh = new Mesh(cross1Geo, this.gravityArrowMaterial);
    cross1Mesh.position.set(positionX, height, positionZ);
    gravityArrowGroup.add(cross1Mesh);

    const cross2Geo = new BoxGeometry(2000, 10, 200);
    const cross2Mesh = new Mesh(cross2Geo, this.gravityArrowMaterial);
    cross2Mesh.position.set(positionX, height, positionZ);
    cross2Mesh.rotation.y = Math.PI / 2;
    gravityArrowGroup.add(cross2Mesh);
    let lineHeight = this.type === 4 ? height * 0.6 : height / 2;
    if (isCog) {
      lineHeight = height - cogY;
    }
    const lineGeo = new CylinderGeometry(70, 70, lineHeight);
    const lineMesh = new Mesh(lineGeo, this.gravityArrowMaterial);
    lineMesh.position.set(positionX, height - lineHeight / 2, positionZ);
    gravityArrowGroup.add(lineMesh);

    const tipGeo = new ConeGeometry(400, 400);
    const tipMesh = new Mesh(tipGeo, this.gravityArrowMaterial);
    tipMesh.position.set(positionX, height - 100 - lineHeight, positionZ);
    tipMesh.rotation.z = Math.PI;
    gravityArrowGroup.add(tipMesh);
    return gravityArrowGroup;
  }

  protected drawAirContainer(): Object3D {
    const group: Group = new Group();
    group.name = 'AirContainer';
    let geometryPoints: Vector3[];
    const parallelLineGeo = new BufferGeometry().setFromPoints([new Vector3(0, 0, -this.airWidth), new Vector3(0, 0, 0)]);
    if (this.airType === 2) {
      const parallelLine7 = new Line(parallelLineGeo, this.lineMaterial);
      parallelLine7.rotation.set(0, -Math.PI / 2, 0);
      parallelLine7.position.set(
        0,
        this.airHeightC - this.airHeightD,
        this.airBaseB + (this.airLengthA - this.airBaseB),
      );
      group.add(parallelLine7);
      const parallelLine8 = new Line(parallelLineGeo, this.lineMaterial);
      parallelLine8.rotation.set(0, -Math.PI / 2, 0);
      parallelLine8.position.set(0, this.airHeightC, 0);
      group.add(parallelLine8);
      const parallelLine9 = new Line(parallelLineGeo, this.lineMaterial);
      parallelLine9.rotation.set(0, -Math.PI / 2, 0);
      parallelLine9.position.set(
        0,
        this.airHeightC,
        this.airBaseB + (this.airLengthA - this.airBaseB),
      );
      group.add(parallelLine9);
      geometryPoints = [
        new Vector3(this.airBaseB, 0, 0),
        new Vector3(this.airLengthA, this.airHeightC - this.airHeightD, 0),
        new Vector3(this.airLengthA, this.airHeightC, 0),
        new Vector3(0, this.airHeightC, 0),
        new Vector3(0, 0, 0),
        new Vector3(this.airBaseB, 0, 0),
      ];
    } else {
      const parallelLine3 = new Line(parallelLineGeo, this.lineMaterial);
      parallelLine3.rotation.set(0, -Math.PI / 2, 0);
      parallelLine3.position.set(
        0,
        this.airHeightC - this.airHeightD,
        -(this.airLengthA - this.airBaseB) / 2,
      );
      const parallelLine4 = new Line(parallelLineGeo, this.lineMaterial);
      parallelLine4.rotation.set(0, -Math.PI / 2, 0);
      parallelLine4.position.set(0, this.airHeightC, -(this.airLengthA - this.airBaseB) / 2);
      const parallelLine5 = new Line(parallelLineGeo, this.lineMaterial);
      parallelLine5.rotation.set(0, -Math.PI / 2, 0);
      parallelLine5.position.set(
        0,
        this.airHeightC - this.airHeightD,
        this.airBaseB + (this.airLengthA - this.airBaseB) / 2,
      );
      const parallelLine6 = new Line(parallelLineGeo, this.lineMaterial);
      parallelLine6.rotation.set(0, -Math.PI / 2, 0);
      parallelLine6.position.set(
        0,
        this.airHeightC,
        this.airBaseB + (this.airLengthA - this.airBaseB) / 2,
      );
      group.add(parallelLine3);
      group.add(parallelLine4);
      group.add(parallelLine5);
      group.add(parallelLine6);
      geometryPoints = [
        new Vector3(this.airBaseB, 0, 0),
        new Vector3(
          this.airBaseB + (this.airLengthA - this.airBaseB) / 2,
          this.airHeightC - this.airHeightD,
          0,
        ),
        new Vector3(this.airBaseB + (this.airLengthA - this.airBaseB) / 2, this.airHeightC, 0),
        new Vector3(-(this.airLengthA - this.airBaseB) / 2, this.airHeightC, 0),
        new Vector3(-(this.airLengthA - this.airBaseB) / 2, this.airHeightC - this.airHeightD, 0),
        new Vector3(0, 0, 0),
        new Vector3(this.airBaseB, 0, 0),
      ];
    }
    const geometry = new BufferGeometry().setFromPoints(geometryPoints);
    const line = new Line(geometry, this.lineMaterial);
    const line1 = new Line(geometry, this.lineMaterial);
    const parallelLine = new Line(parallelLineGeo, this.lineMaterial);
    line.rotation.set(0, -Math.PI / 2, 0);
    line1.position.set(this.airWidth, 0, 0);
    line1.rotation.set(0, -Math.PI / 2, 0);
    parallelLine.rotation.set(0, -Math.PI / 2, 0);
    const parallelLine2 = new Line(parallelLineGeo, this.lineMaterial);
    parallelLine2.rotation.set(0, -Math.PI / 2, 0);
    parallelLine2.position.set(0, 0, this.airBaseB);
    line.renderOrder = 1;
    line1.renderOrder = 1;
    parallelLine.renderOrder = 1;
    group.add(line);
    group.add(line1);
    group.add(parallelLine);
    group.add(parallelLine2);

    const rightCorner = this.airLengthA - (this.airLengthA - this.airBaseB) / 2;

    const leftGeometry = new PlaneGeometry(this.airLengthA, this.airHeightC);
    leftGeometry.translate(this.airLengthA / 2, this.airHeightC / 2, 0);
    const leftMesh = new Mesh(leftGeometry, this.meshTransparentMaterial);
    leftMesh.position.set(0, 0, rightCorner);
    leftMesh.rotation.set(0, Math.PI / 2, 0);
    leftMesh.name = 'leftSide';
    group.add(leftMesh);

    const rightGeometry = new PlaneGeometry(this.airLengthA, this.airHeightC);
    rightGeometry.translate(this.airLengthA / 2, this.airHeightC / 2, 0);
    const rightMesh = new Mesh(rightGeometry, this.meshTransparentMaterial);
    rightMesh.position.set(this.airWidth, 0, rightCorner);
    rightMesh.rotation.set(0, Math.PI / 2, 0);
    rightMesh.name = 'rightSide';
    group.add(rightMesh);

    const frontGeometry = new PlaneGeometry(this.airWidth, this.airHeightC);
    frontGeometry.translate(this.airWidth / 2, this.airHeightC / 2, 0);
    const frontMesh = new Mesh(frontGeometry, this.meshTransparentMaterial);
    frontMesh.position.set(0, 0, rightCorner);
    frontMesh.rotation.set(0, 0, 0);
    frontMesh.name = 'frontSide';
    group.add(frontMesh);

    const backGeometry = new PlaneGeometry(this.airWidth, this.airHeightC);
    backGeometry.translate(this.airWidth / 2, this.airHeightC / 2, 0);
    const backMesh = new Mesh(backGeometry, this.meshTransparentMaterial);
    backMesh.position.set(0, 0, -(this.airLengthA - this.airBaseB) / 2);
    backMesh.rotation.set(0, 0, 0);
    backMesh.name = 'backSide';
    group.add(backMesh);

    return group;
  }

  protected drawSides(): Object3D {
    const group = new Group();
    group.name = 'Sides';
    const sideGeometry = new BufferGeometry().setFromPoints([
      new Vector3(this.length, this.height, 0),
      new Vector3(this.length, 0, 0),
      new Vector3(0, 0, 0),
      new Vector3(0, this.height, 0),
      new Vector3(this.length, this.height, 0),
    ]);
    const lineMaterial = this.view === 'setLeft' ? this.lineGreyMaterial : this.lineMaterial;
    const leftSide = new Line(sideGeometry, lineMaterial);
    const rightSide = new Line(sideGeometry, lineMaterial);
    leftSide.position.set(0, 0, 0);
    leftSide.rotation.set(0, -Math.PI / 2, 0);
    rightSide.position.set(this.width, 0, 0);
    rightSide.rotation.set(0, -Math.PI / 2, 0);
    leftSide.renderOrder = 1;
    rightSide.renderOrder = 1;
    leftSide.name = 'leftSide';
    rightSide.name = 'rightSide';
    group.add(leftSide);
    group.add(rightSide);
    const backGeometry = new BufferGeometry().setFromPoints([
      new Vector3(0, this.height, this.length),
      new Vector3(this.width, this.height, this.length),
    ]);
    const backLoadingLine = new Line(backGeometry, lineMaterial);
    group.add(backLoadingLine);
    this.addSideLoadingMarkup(
      this.width,
      this.height,
      this.length,
      this.trailerLoadingSide,
      group,
      false,
    );
    return group;
  }

  private addSideLoadingMarkup(
    width: number,
    height: number,
    length: number,
    sideLoading: string,
    group: Group,
    isSet: boolean,
  ): void {
    const position = isSet ? this.length + this.gap : 0;
    if (sideLoading === 'left') {
      const loadingMarkup = this.generateLoadingMarkup(length, height, this.markupMaterial);
      const setWidth = isSet ? (this.width - width) / 2 : 0;
      loadingMarkup.position.set(setWidth, 0, position);
      loadingMarkup.rotation.set(0, Math.PI / 2, 0);
      group.add(loadingMarkup);
    }
    if (sideLoading === 'right') {
      const loadingMarkup = this.generateLoadingMarkup(length, height, this.markupMaterial);
      const setWidth = isSet ? (this.width - width) / 2 + width : width;
      loadingMarkup.position.set(setWidth, 0, position);
      loadingMarkup.rotation.set(0, Math.PI / 2, 0);
      group.add(loadingMarkup);
    }
    if (sideLoading === 'back') {
      const loadingMarkup = this.generateLoadingMarkup(width, height, this.markupMaterial);
      const setWidth = isSet ? (this.width - width) / 2 + width : width;
      loadingMarkup.position.set(setWidth, 0, length + position);
      loadingMarkup.rotation.set(0, 0, 0);
      group.add(loadingMarkup);
    }
    if (sideLoading === 'top') {
      const loadingMarkup = this.generateLoadingMarkup(length, width, this.markupMaterial);
      const setWidth = isSet ? (this.width - width) / 2 + width : width;
      loadingMarkup.position.set(setWidth, height, length + position);
      loadingMarkup.rotation.set(Math.PI / 2, 0, Math.PI / 2);
      group.add(loadingMarkup);
    }
    group.name = 'Markup';
  }

  private generateLoadingMarkup(
    length: number,
    height: number,
    material: MeshBasicMaterial,
    isDoor = false,
  ): Object3D {
    const group = new Group();
    group.name = 'markup';
    const lineWidth = this.unit !== 'lb' ? 200 : 200 / 2.4;
    const lineDepth = lineWidth;

    const boxLeftGeometry = new BoxGeometry(height, lineWidth, lineDepth);
    boxLeftGeometry.translate(-height / 2, -lineWidth / 2, 0);
    const boxLeftMesh = new Mesh(boxLeftGeometry, material);
    boxLeftMesh.name = 'boxLeftMesh';
    boxLeftMesh.rotation.z = Math.PI / 2;
    boxLeftMesh.position.set(-lineWidth / 2, height, 0);
    group.add(boxLeftMesh);

    const boxTopGeometry = new BoxGeometry(length + lineWidth / 2, lineWidth, lineDepth);
    boxTopGeometry.translate(-length / 2 - lineWidth / 4, 0, 0);
    const boxTopMesh = new Mesh(boxTopGeometry, material);
    boxTopMesh.name = 'boxTopMesh';
    boxTopMesh.position.set(lineWidth / 2, height, 0);
    group.add(boxTopMesh);

    const boxRightGeometry = boxLeftGeometry;
    const boxRightMesh = new Mesh(boxRightGeometry, material);
    boxRightMesh.name = 'boxRightMesh';
    boxRightMesh.rotation.z = Math.PI / 2;
    boxRightMesh.position.set(-length, height, 0);
    group.add(boxRightMesh);

    if (isDoor) {
      const boxCenterGeometry = boxLeftGeometry;
      const boxCenterMesh = new Mesh(boxCenterGeometry, material);
      boxCenterMesh.name = 'boxCenterMesh';
      boxCenterMesh.rotation.z = Math.PI / 2;
      boxCenterMesh.position.set(-length / 2, height, 0);
      group.add(boxCenterMesh);
    }

    const boxBottomGeometry = boxTopGeometry;
    const boxBottomMesh = new Mesh(boxBottomGeometry, material);
    boxBottomMesh.name = 'boxBottomMesh';
    boxBottomMesh.position.set(lineWidth / 2, 0, 0);
    group.add(boxBottomMesh);

    return group;
  }

  protected drawSetSides(): Object3D {
    const group = new Group();
    group.name = 'SetSides';
    const geometry = new BufferGeometry().setFromPoints([
      new Vector3(this.length1, this.height1, 0),
      new Vector3(this.length1, 0, 0),
      new Vector3(0, 0, 0),
      new Vector3(0, this.height1, 0),
      new Vector3(this.length1, this.height1, 0),
    ]);
    const lineMaterial = this.view === 'left' ? this.lineGreyMaterial : this.lineMaterial;
    const leftSide = new Line(geometry, lineMaterial);
    leftSide.name = 'leftSide';
    const rightSide = new Line(geometry, lineMaterial);
    rightSide.name = 'rightSide';
    leftSide.position.set(this.width1 + (this.width - this.width1) / 2, 0, this.length + this.gap);
    leftSide.rotation.set(0, -Math.PI / 2, 0);
    rightSide.position.set((this.width - this.width1) / 2, 0, this.length + this.gap);
    rightSide.rotation.set(0, -Math.PI / 2, 0);
    leftSide.renderOrder = 1;
    rightSide.renderOrder = 1;
    group.add(leftSide);
    group.add(rightSide);
    const backGeometry = new BufferGeometry().setFromPoints([
      new Vector3(0, this.height1, this.length1),
      new Vector3(this.width1, this.height1, this.length1),
    ]);
    const backLoadingLine = new Line(backGeometry, lineMaterial);
    backLoadingLine.position.set((this.width - this.width1) / 2, 0, this.length + this.gap);
    group.add(backLoadingLine);
    this.addSideLoadingMarkup(
      this.width1,
      this.height1,
      this.length1,
      this.semiTrailerLoadingSide,
      group,
      true,
    );
    return group;
  }

  protected drawFront(): Object3D {
    const geometry = new BufferGeometry().setFromPoints([
      new Vector3(this.width, this.height, 0),
      new Vector3(this.width, 0, 0),
      new Vector3(0, 0, 0),
      new Vector3(0, this.height, 0),
      new Vector3(this.width, this.height, 0),
    ]);
    const lineMaterial = this.view === 'setLeft' ? this.lineGreyMaterial : this.lineMaterial;
    const line = new Line(geometry, lineMaterial);
    line.name = 'Front';
    return line;
  }

  protected drawSetFront(): Object3D {
    const geometry = new BufferGeometry().setFromPoints([
      new Vector3(this.width1, this.height1, 0),
      new Vector3(this.width1, 0, 0),
      new Vector3(0, 0, 0),
      new Vector3(0, this.height1, 0),
      new Vector3(this.width1, this.height1, 0),
    ]);
    const line = new Line(geometry, this.lineMaterial);
    line.position.set((this.width - this.width1) / 2, 0, this.length + this.gap);
    line.name = 'SetFront';
    return line;
  }

  protected drawMeasureVertical(): Object3D {
    const length: number = this.airBaseB ? this.airBaseB : this.length;
    const measure: Group = this.drawMeasure(length);
    const plane: Object3D = measure.getObjectByName('measurePlane');
    let zLength: number = this.airBaseB ? (this.airLengthA - this.airBaseB) / 2 : 0;
    zLength = this.airType === 2 ? 0 : zLength;
    plane.position.set(-this.width * 0.001, 0, zLength);
    plane.rotation.set(0, Math.PI / 2, Math.PI / 2);
    plane.name = 'planeVertical';

    const grid: Object3D = measure.getObjectByName('grid');
    grid.position.set(-this.width * 0.001, 0, zLength);
    grid.name = 'gridVertical';

    return measure;
  }

  protected drawMeasureHorizontal(): Object3D {
    const width: number = this.airWidth ? this.airWidth : this.width;
    const length: number = this.airBaseB ? this.airBaseB : this.length;
    const lineLength: number = this.width !== 0 ? width : length;
    const measure: Group = this.drawMeasure(lineLength);
    const plane: Object3D = measure.getObjectByName('measurePlane');
    let airPosition: number = this.airBaseB ? (this.airLengthA - this.airBaseB) / 2 : 0;
    airPosition = this.airType === 2 ? 0 : airPosition;
    plane.position.set(0, 0, length + length * 0.001 + airPosition);
    plane.rotation.set(Math.PI, 0, Math.PI * 1.5);
    plane.name = 'planeHorizontal';

    const grid: Object3D = measure.getObjectByName('grid');
    grid.position.set(0, 0, length + length * 0.001 + airPosition);
    grid.rotation.set(0, Math.PI / 2, 0);
    grid.name = 'gridHorizontal';

    return measure;
  }

  private getMeasureWidth(): number {
    const isPallet: boolean = this.type === 3;
    return this.unit === 'cm' ? this.MEASURE_WIDTH : this.MEASURE_WIDTH / (isPallet ? 6.54 : 2.54);
  }

  protected drawMeasureHeight(): Object3D {
    const isPallet: boolean = this.type === 3;
    const measureWidth: number = this.getMeasureWidth();
    const height: number = isPallet ? this.palletMaxHeight - this.palletHeight : this.height;
    const measure: Group = this.drawMeasure(height, true);
    const plane: Object3D = measure.getObjectByName('measurePlane');
    const zLength: number = measureWidth;
    const yPos: number = measureWidth + measureWidth / 2;
    plane.rotation.set(-Math.PI / 2, Math.PI / 2, Math.PI / 2);
    plane.position.set(-this.width * 0.001, yPos, zLength);
    plane.name = 'planeHeight';
    const grid: Object3D = measure.getObjectByName('grid');
    grid.position.set(-this.width * 0.001, yPos, zLength);
    grid.rotateX(-Math.PI / 2);
    grid.translateZ(-measureWidth / 2);
    grid.name = 'gridHeight';
    return measure;
  }

  protected drawSetMeasureHeight(): Object3D {
    const measure: Group = this.drawMeasure(this.height1, true);
    const plane: Object3D = measure.getObjectByName('measurePlane');
    const measureWidth: number = this.getMeasureWidth();
    const zLength: number = measureWidth + this.length + this.gap;
    const yPos: number = measureWidth + measureWidth / 2;
    plane.rotation.set(-Math.PI / 2, Math.PI / 2, Math.PI / 2);
    plane.position.set(-this.width * 0.001, yPos, zLength);
    plane.name = 'planeHeight';
    const grid: Object3D = measure.getObjectByName('grid');
    grid.position.set(-this.width * 0.001, yPos, zLength);
    grid.rotateX(-Math.PI / 2);
    return measure;
  }

  protected drawSetMeasureVertical(): Object3D {
    const measure = this.drawMeasure(this.length1);
    const plane = measure.getObjectByName('measurePlane');
    plane.position.set(-this.width1 / 2 + this.width / 2, 0, this.length + this.gap);
    plane.rotation.set(0, Math.PI / 2, Math.PI / 2);
    plane.name = 'planeSetVertical';

    const grid = measure.getObjectByName('grid');
    grid.position.set(-this.width1 / 2 + this.width / 2, 0, this.length + this.gap);
    grid.name = 'gridSetVertical';

    return measure;
  }

  protected drawSetMeasureHorizontal(): Object3D {
    const measure = this.drawMeasure(this.width1);
    const plane = measure.getObjectByName('measurePlane');
    plane.position.set(
      -this.width1 / 2 + this.width / 2,
      0,
      this.length + this.length1 + this.gap + this.length * 0.001,
    );

    plane.rotation.set(Math.PI, 0, Math.PI * 1.5);
    plane.name = 'planeSetHorizontal';

    const grid = measure.getObjectByName('grid');
    grid.position.set(
      -this.width1 / 2 + this.width / 2,
      0,
      this.length + this.length1 + this.gap + this.length * 0.001,
    );

    grid.rotation.set(0, Math.PI / 2, 0);
    grid.name = 'gridSetHorizontal';

    return measure;
  }

  protected drawMeasure(length: number, isHeight = false): Group {
    this.measureGroup = this.measureGroup ? this.measureGroup : new Group();
    this.measureGroup.name = 'Measure';
    const yHeight: number = this.palletHeight ? this.palletHeight : 0;
    const measureWidth: number = this.getMeasureWidth();
    const planeHeight: number = measureWidth;
    const geometryPlane: PlaneGeometry = new PlaneGeometry(
      planeHeight,
      isHeight ? length - measureWidth : length,
    );
    const isHeightTranslate: number = isHeight ? length / 2 - measureWidth : length / 2;
    geometryPlane.translate(-planeHeight / 2 + planeHeight, isHeightTranslate, 0);
    const plane = new Mesh<PlaneGeometry, MeshBasicMaterial>(
      geometryPlane,
      this.frontMaterial,
    );
    plane.name = 'measurePlane';
    let vertices = [];
    const space = this.unit === 'cm' ? 2000 : 10 * 100;
    vertices = vertices.concat(
      this.calculateVerticesForHorizontalLines(
        planeHeight / 2,
        isHeight ? length - measureWidth : length,
        space,
        isHeight,
      ),
    );
    const geometry: BufferGeometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(vertices, 3));
    const gridMaterial: LineBasicMaterial = new LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
    });
    const grid = new LineSegments<BufferGeometry, LineBasicMaterial>(
      geometry,
      gridMaterial,
    );
    grid.name = 'grid';
    this.measureGroup.add(grid);
    this.measureGroup.add(plane);
    this.measureGroup.position.setY(yHeight);
    return this.measureGroup;
  }

  private firstPartPosition(): Vector3 {
    return new Vector3(0, 0, 0);
  }

  private secondPartPosition(): Vector3 {
    return new Vector3(Math.round(this.width / 2 - this.width1 / 2), 0, this.length + this.gap);
  }

  private calculateVerticesForHorizontalLines(
    lineLength: number,
    axisLength: number,
    space: number,
    isHeight = false,
  ): number[] {
    lineLength = lineLength !== 0 ? lineLength : 750;
    const vertices = [];
    for (let i = 0; axisLength - space * i >= 0; i++) {
      if (isHeight) {
        vertices.push(0, lineLength + lineLength, i * space, 0, lineLength, i * space);
      } else {
        vertices.push(0, 0, i * space, 0, lineLength, i * space);
      }
    }
    return vertices as number[];
  }
}

export class Truck extends LoadingSpace {
  protected drawFront(): Group {
    const group = new Group();
    group.name = 'Front';
    if (this.width === 0 && this.height === 0) {
      return group;
    }
    const geometry = new PlaneGeometry(this.width, this.height);
    geometry.translate(this.width / 2, this.height / 2, 0);
    const edgesGeometry = new EdgesGeometry(geometry);
    const lineMaterial = this.view === 'setLeft' ? this.lineGreyMaterial : this.lineMaterial;
    const plane = new LineSegments(edgesGeometry, lineMaterial);
    // Hack that solves z-fighting problem
    plane.position.set(0, 0, -this.length * 0.001);
    plane.renderOrder = 1;
    group.add(plane);
    const cabin = this.generateCabin(
      this.dimensionsOfCabin.length,
      this.dimensionsOfCabin.width,
      this.dimensionsOfCabin.height,
      this.dimensionsOfCabin.height1,
      this.dimensionsOfCabin.height2,
      this.dimensionsOfCabin.length1,
      this.dimensionsOfCabin.gap,
    );
    cabin.renderOrder = 2;
    group.add(cabin);
    return group;
  }

  private generateCabin(
    cabinLength: number,
    cabinWidth: number,
    cabinHeight: number,
    height1: number,
    height2: number,
    length1: number,
    gap: number,
  ): Object3D {
    const group = new Group();
    group.name = 'Cabin';
    const cabinGeometry = new BufferGeometry().setFromPoints([
      new Vector3(0, 0, 0),
      new Vector3(0, 0, -cabinLength),
      new Vector3(0, height1, -cabinLength),
      new Vector3(0, height1 + cabinHeight, -(cabinLength - length1)),
      new Vector3(0, height1 + cabinHeight + height2, 0),
      new Vector3(0, 0, 0),
    ]);
    const lineMaterial = this.view === 'setLeft' ? this.lineGreyMaterial : this.lineMaterial;
    const leftCabin = new Line(cabinGeometry, lineMaterial);
    const rightCabin = new Line(cabinGeometry, lineMaterial);
    rightCabin.position.set(cabinWidth, 0, 0);
    const parallelLinesGeometry = new BufferGeometry().setFromPoints([
      new Vector3(0, 0, -cabinLength),
      new Vector3(cabinWidth, 0, -cabinLength),
    ]);
    const parallelLine = new Line(parallelLinesGeometry, lineMaterial);
    const parallelLine1 = new Line(parallelLinesGeometry, lineMaterial);
    const parallelLine2 = new Line(parallelLinesGeometry, lineMaterial);
    const parallelLine3 = new Line(parallelLinesGeometry, lineMaterial);
    const parallelLine4 = new Line(parallelLinesGeometry, lineMaterial);
    parallelLine1.position.set(0, 0, cabinLength);
    parallelLine2.position.set(0, height1, 0);
    parallelLine3.position.set(0, height1 + cabinHeight, length1);
    parallelLine4.position.set(0, height1 + cabinHeight + height2, cabinLength);
    group.add(parallelLine);
    group.add(parallelLine1);
    group.add(parallelLine2);
    group.add(parallelLine3);
    group.add(parallelLine4);
    group.add(leftCabin);
    group.add(rightCabin);
    const center = -cabinWidth / 2 + this.width / 2;
    group.position.set(center, 25, -gap);
    return group;
  }

  protected drawDoors(): Group {
    return new Group();
  }

  protected drawSetFront(): Group {
    return new Group();
  }

  protected drawBack(): Group {
    return new Group();
  }

  protected drawSetBack(): Group {
    return new Group();
  }
}

export class AirContainer extends LoadingSpace {
  protected drawFront(): Mesh<PlaneGeometry, MeshBasicMaterial> {
    const geometry = new PlaneGeometry(this.width, this.height);
    geometry.translate(this.width / 2, this.height / 2, 0);
    const plane = new Mesh(geometry, this.frontMaterial);
    // Hack that solves z-fighting problem
    plane.position.set(0, 0, -this.length * 0.001);
    return plane;
  }

  protected drawDoors(): Group {
    return new Group();
  }

  protected drawBack(): Group {
    return new Group();
  }

  protected drawSetBack(): Group {
    return new Group();
  }
}

export class Container extends LoadingSpace {
  protected drawFront(): Mesh<PlaneGeometry, MeshBasicMaterial> {
    const geometry = new PlaneGeometry(this.width, this.height);
    geometry.translate(this.width / 2, this.height / 2, 0);
    const plane = new Mesh(geometry, this.frontMaterial);
    // Hack that solves z-fighting problem
    plane.position.set(0, 0, -this.length * 0.001);
    return plane;
  }

  protected drawBack(): Group {
    return new Group();
  }

  protected drawSetBack(): Group {
    return new Group();
  }

  protected drawAxles(): Group {
    return new Group();
  }
}

export class OtherLoadingSpace extends LoadingSpace {
  protected drawDoors(): Group {
    return new Group();
  }

  protected drawAxles(): Group {
    return new Group();
  }
}

export interface CoolerData {
  coolerTrailerWidth: number;
  coolerTrailerHeight: number;
  coolerTrailerLength: number;
  coolerTrailerFloorHeight: number;
  coolerSemiTrailerWidth: number;
  coolerSemiTrailerHeight: number;
  coolerSemiTrailerLength: number;
  coolerSemiTrailerFloorHeight: number;
}

export interface WheelData {
  wheelTrailerWidth: number;
  wheelTrailerHeight: number;
  wheelTrailerLength: number;
  wheelTrailerDistance: number;
  wheelSemiTrailerWidth: number;
  wheelSemiTrailerHeight: number;
  wheelSemiTrailerLength: number;
  wheelSemiTrailerDistance: number;
}
